import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Phone, Clock, Calendar as CalendarIcon, ChevronLeft, Star, GraduationCap, Globe, Shield, Send, Copy, Check, UserCheck, ChevronDown, HelpCircle, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { format, isBefore, startOfDay, parseISO } from 'date-fns';
import { fr, ar } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPECIALTIES = {
  general: { name_fr: 'Médecine générale', name_ar: 'طب عام' },
  cardiology: { name_fr: 'Cardiologie', name_ar: 'أمراض القلب' },
  dermatology: { name_fr: 'Dermatologie', name_ar: 'الأمراض الجلدية' },
  gynecology: { name_fr: 'Gynécologie', name_ar: 'أمراض النساء' },
  ophthalmology: { name_fr: 'Ophtalmologie', name_ar: 'طب العيون' },
  pediatrics: { name_fr: 'Pédiatrie', name_ar: 'طب الأطفال' },
  dentistry: { name_fr: 'Dentiste', name_ar: 'طب الأسنان' },
  orthopedics: { name_fr: 'Orthopédie', name_ar: 'جراحة العظام' },
  psychiatry: { name_fr: 'Psychiatrie', name_ar: 'الطب النفسي' },
  neurology: { name_fr: 'Neurologie', name_ar: 'الأمراض العصبية' },
  urology: { name_fr: 'Urologie', name_ar: 'المسالك البولية' },
  ent: { name_fr: 'ORL', name_ar: 'أنف أذن حنجرة' },
  radiology: { name_fr: 'Radiologie', name_ar: 'الأشعة' },
  gastroenterology: { name_fr: 'Gastro-entérologie', name_ar: 'أمراض الجهاز الهضمي' },
  pneumology: { name_fr: 'Pneumologie', name_ar: 'أمراض الرئة' },
  general_surgery: { name_fr: 'Chirurgie générale', name_ar: 'الجراحة العامة' },
  endocrinology: { name_fr: 'Endocrinologie', name_ar: 'الغدد الصماء' },
  anesthesiology: { name_fr: 'Anesthésiologie', name_ar: 'التخدير' },
  rheumatology: { name_fr: 'Rhumatologie', name_ar: 'أمراض الروماتيزم' },
  physical_medicine: { name_fr: 'Médecine physique', name_ar: 'الطب الطبيعي' },
  nephrology: { name_fr: 'Néphrologie', name_ar: 'أمراض الكلى' },
  neurosurgery: { name_fr: 'Neurochirurgie', name_ar: 'جراحة المخ والأعصاب' },
  internal_medicine: { name_fr: 'Médecine interne', name_ar: 'الطب الباطني' },
  allergology: { name_fr: 'Allergologie', name_ar: 'أمراض الحساسية' },
  hematology: { name_fr: 'Hématologie', name_ar: 'أمراض الدم' },
  speech_therapy: { name_fr: 'Orthophonie', name_ar: 'علاج النطق' },
  physiotherapy: { name_fr: 'Kinésithérapie', name_ar: 'العلاج الطبيعي' },
  psychology: { name_fr: 'Psychologie', name_ar: 'علم النفس' },
  aesthetic_medicine: { name_fr: 'Médecine esthétique', name_ar: 'الطب التجميلي' },
};

const WILAYAS = {
  '01': 'Adrar', '02': 'Chlef', '03': 'Laghouat', '04': 'Oum El Bouaghi', '05': 'Batna',
  '06': 'Béjaïa', '07': 'Biskra', '08': 'Béchar', '09': 'Blida', '10': 'Bouira',
  '11': 'Tamanrasset', '12': 'Tébessa', '13': 'Tlemcen', '14': 'Tiaret', '15': 'Tizi Ouzou',
  '16': 'Alger', '17': 'Djelfa', '18': 'Jijel', '19': 'Sétif', '20': 'Saïda',
  '21': 'Skikda', '22': 'Sidi Bel Abbès', '23': 'Annaba', '24': 'Guelma', '25': 'Constantine',
  '26': 'Médéa', '27': 'Mostaganem', '28': "M'Sila", '29': 'Mascara', '30': 'Ouargla',
  '31': 'Oran', '32': 'El Bayadh', '33': 'Illizi', '34': 'Bordj Bou Arréridj', '35': 'Boumerdès',
  '36': 'El Tarf', '37': 'Tindouf', '38': 'Tissemsilt', '39': 'El Oued', '40': 'Khenchela',
  '41': 'Souk Ahras', '42': 'Tipaza', '43': 'Mila', '44': 'Aïn Defla', '45': 'Naâma',
  '46': 'Aïn Témouchent', '47': 'Ghardaïa', '48': 'Relizane',
  '49': 'Timimoun', '50': 'Bordj Badji Mokhtar', '51': 'Ouled Djellal', '52': 'Béni Abbès', '53': 'In Salah',
  '54': 'In Guezzam', '55': 'Touggourt', '56': 'Djanet', '57': 'El Meghaier', '58': 'El Menia',
  '59': 'Aflou', '60': 'Ain Oussera', '61': 'Barika', '62': 'Bir El Ater', '63': 'Bou Saada',
  '64': 'El Abiodh Sidi Cheikh', '65': 'El Aricha', '66': 'El Kantara', '67': 'Ksar Chellala', '68': 'Ksar El Boukhari',
  '69': 'Messaad',
};

// Motifs de consultation frequents par specialite (FR/AR), pour la FAQ de la
// fiche medecin. Fallback generique si la specialite n'est pas listee ici.
const CONSULT_REASONS = {
  psychiatry: {
    fr: ['anxiété', 'dépression', 'troubles du sommeil', 'suivi psychiatrique', 'troubles de l\'humeur'],
    ar: ['القلق', 'الاكتئاب', 'اضطرابات النوم', 'متابعة نفسية', 'اضطرابات المزاج'],
  },
  cardiology: {
    fr: ['douleurs thoraciques', 'hypertension', 'palpitations', 'essoufflement', 'bilan cardiaque'],
    ar: ['آلام الصدر', 'ارتفاع ضغط الدم', 'خفقان القلب', 'ضيق التنفس', 'فحص القلب'],
  },
  dermatology: {
    fr: ['problèmes de peau', 'acné', 'allergies cutanées', 'grains de beauté', 'eczéma'],
    ar: ['مشاكل الجلد', 'حب الشباب', 'حساسية الجلد', 'الشامات', 'الأكزيما'],
  },
  gynecology: {
    fr: ['suivi de grossesse', 'contraception', 'douleurs pelviennes', 'consultation gynécologique', 'ménopause'],
    ar: ['متابعة الحمل', 'وسائل منع الحمل', 'آلام الحوض', 'استشارة أمراض النساء', 'سن اليأس'],
  },
  pediatrics: {
    fr: ['suivi de croissance', 'vaccination', "fièvre chez l'enfant", 'consultation pédiatrique', 'troubles digestifs'],
    ar: ['متابعة النمو', 'التلقيح', 'حمى عند الطفل', 'استشارة طب الأطفال', 'اضطرابات الهضم'],
  },
  dentistry: {
    fr: ['carie dentaire', 'douleur dentaire', 'détartrage', 'soins dentaires', 'implant dentaire'],
    ar: ['تسوس الأسنان', 'ألم الأسنان', 'إزالة الجير', 'العناية بالأسنان', 'زراعة الأسنان'],
  },
  ophthalmology: {
    fr: ['baisse de la vision', 'contrôle de la vue', 'sécheresse oculaire', 'conjonctivite', 'prescription de lunettes'],
    ar: ['ضعف النظر', 'فحص النظر', 'جفاف العين', 'التهاب الملتحمة', 'وصفة النظارات'],
  },
  general: {
    fr: ['consultation générale', "renouvellement d'ordonnance", 'certificat médical', 'bilan de santé', 'symptômes divers'],
    ar: ['استشارة عامة', 'تجديد الوصفة', 'شهادة طبية', 'فحص صحي', 'أعراض متنوعة'],
  },
  orthopedics: {
    fr: ['douleurs articulaires', 'entorses et fractures', 'douleurs dorsales', 'douleurs du dos', 'suivi post-opératoire'],
    ar: ['آلام المفاصل', 'الالتواءات والكسور', 'آلام الظهر', 'آلام العمود الفقري', 'متابعة ما بعد الجراحة'],
  },
  neurology: {
    fr: ['maux de tête chroniques', 'vertiges', 'troubles de la mémoire', 'engourdissements', 'épilepsie'],
    ar: ['الصداع المزمن', 'الدوخة', 'اضطرابات الذاكرة', 'التنميل', 'الصرع'],
  },
  urology: {
    fr: ['troubles urinaires', 'calculs rénaux', 'infections urinaires', 'troubles de la prostate', 'douleurs pelviennes'],
    ar: ['اضطرابات المسالك البولية', 'حصى الكلى', 'التهابات المسالك البولية', 'اضطرابات البروستاتا', 'آلام الحوض'],
  },
  ent: {
    fr: ["maux de gorge", 'sinusite', 'troubles de l\'audition', 'vertiges', 'problèmes de nez'],
    ar: ['التهاب الحلق', 'التهاب الجيوب الأنفية', 'اضطرابات السمع', 'الدوخة', 'مشاكل الأنف'],
  },
  radiology: {
    fr: ['bilan d\'imagerie', 'radiographie', 'échographie', 'scanner', 'IRM'],
    ar: ['فحص بالأشعة', 'تصوير بالأشعة السينية', 'الموجات فوق الصوتية', 'الفحص المقطعي', 'الرنين المغناطيسي'],
  },
  gastroenterology: {
    fr: ['douleurs abdominales', 'troubles digestifs', 'reflux gastrique', 'troubles intestinaux', 'bilan hépatique'],
    ar: ['آلام البطن', 'اضطرابات الهضم', 'ارتجاع المعدة', 'اضطرابات الأمعاء', 'فحص الكبد'],
  },
  pneumology: {
    fr: ['toux persistante', 'essoufflement', 'asthme', 'bilan respiratoire', 'apnée du sommeil'],
    ar: ['السعال المستمر', 'ضيق التنفس', 'الربو', 'فحص الجهاز التنفسي', 'انقطاع النفس أثناء النوم'],
  },
  general_surgery: {
    fr: ['hernie', 'appendicite', 'consultation pré-opératoire', 'suivi post-opératoire', 'masse ou grosseur'],
    ar: ['الفتق', 'التهاب الزائدة الدودية', 'استشارة ما قبل الجراحة', 'متابعة ما بعد الجراحة', 'كتلة أو ورم'],
  },
  endocrinology: {
    fr: ['diabète', 'troubles thyroïdiens', 'prise ou perte de poids inexpliquée', 'fatigue chronique', 'bilan hormonal'],
    ar: ['السكري', 'اضطرابات الغدة الدرقية', 'زيادة أو نقصان الوزن غير المبرر', 'الإرهاق المزمن', 'فحص الهرمونات'],
  },
  anesthesiology: {
    fr: ['consultation pré-anesthésique', 'prise en charge de la douleur', 'préparation à une intervention'],
    ar: ['استشارة ما قبل التخدير', 'علاج الألم', 'التحضير لعملية جراحية'],
  },
  rheumatology: {
    fr: ['douleurs articulaires', 'arthrose', 'arthrite', 'douleurs musculaires', 'ostéoporose'],
    ar: ['آلام المفاصل', 'خشونة المفاصل', 'التهاب المفاصل', 'آلام العضلات', 'هشاشة العظام'],
  },
  physical_medicine: {
    fr: ['rééducation fonctionnelle', 'suivi post-traumatique', 'douleurs chroniques', 'kinésithérapie', 'récupération motrice'],
    ar: ['إعادة التأهيل الوظيفي', 'متابعة ما بعد الإصابة', 'الآلام المزمنة', 'العلاج الطبيعي', 'استعادة الحركة'],
  },
  nephrology: {
    fr: ['insuffisance rénale', 'calculs rénaux', 'hypertension', 'bilan rénal', 'suivi de dialyse'],
    ar: ['الفشل الكلوي', 'حصى الكلى', 'ارتفاع ضغط الدم', 'فحص الكلى', 'متابعة غسيل الكلى'],
  },
  neurosurgery: {
    fr: ['hernie discale', 'compression nerveuse', 'tumeur cérébrale', 'traumatisme crânien', 'sciatique sévère'],
    ar: ['انزلاق غضروفي', 'ضغط على الأعصاب', 'ورم في الدماغ', 'إصابة في الرأس', 'عرق النسا الحاد'],
  },
  internal_medicine: {
    fr: ['bilan de santé complet', 'maladies chroniques multiples', 'fatigue inexpliquée', 'suivi de plusieurs pathologies'],
    ar: ['فحص صحي شامل', 'أمراض مزمنة متعددة', 'إرهاق غير مبرر', 'متابعة عدة أمراض'],
  },
  allergology: {
    fr: ['allergies saisonnières', 'allergies alimentaires', 'asthme allergique', 'eczéma', 'réactions cutanées'],
    ar: ['الحساسية الموسمية', 'حساسية الطعام', 'الربو التحسسي', 'الأكزيما', 'التفاعلات الجلدية'],
  },
  hematology: {
    fr: ['anémie', 'troubles de la coagulation', 'bilan sanguin anormal', 'fatigue chronique', 'suivi hématologique'],
    ar: ['فقر الدم', 'اضطرابات التخثر', 'فحص دم غير طبيعي', 'الإرهاق المزمن', 'متابعة أمراض الدم'],
  },
  speech_therapy: {
    fr: ['troubles du langage', 'retard de parole', 'bégaiement', 'troubles de la déglutition', 'rééducation vocale'],
    ar: ['اضطرابات اللغة', 'تأخر النطق', 'التلعثم', 'اضطرابات البلع', 'إعادة تأهيل الصوت'],
  },
  physiotherapy: {
    fr: ['rééducation post-traumatique', 'douleurs musculaires', 'récupération après opération', 'raideur articulaire', 'suivi sportif'],
    ar: ['إعادة التأهيل بعد الإصابة', 'آلام العضلات', 'التعافي بعد الجراحة', 'تيبس المفاصل', 'متابعة رياضية'],
  },
  psychology: {
    fr: ['stress', 'anxiété', 'difficultés relationnelles', 'accompagnement psychologique', 'gestion des émotions'],
    ar: ['التوتر', 'القلق', 'صعوبات العلاقات', 'الدعم النفسي', 'إدارة المشاعر'],
  },
  aesthetic_medicine: {
    fr: ['soins esthétiques du visage', 'traitement anti-âge', 'épilation laser', 'rajeunissement cutané', 'consultation esthétique'],
    ar: ['العناية التجميلية بالوجه', 'علاج مكافحة الشيخوخة', 'إزالة الشعر بالليزر', 'تجديد نضارة البشرة', 'استشارة تجميلية'],
  },
};

const DEFAULT_REASONS = {
  fr: ['consultation de suivi', 'bilan de santé', 'symptômes persistants', 'renouvellement de traitement'],
  ar: ['استشارة متابعة', 'فحص صحي', 'أعراض مستمرة', 'تجديد العلاج'],
};

// Slugs utilises par la route /medecins/:wilaya/:specialty (voir DoctorsByWilaya.js)
// pour generer les liens "Autres medecins" en bas de fiche.
const WILAYA_SLUG_BY_CODE = {
  '01': 'adrar', '02': 'chlef', '03': 'laghouat', '04': 'oum-el-bouaghi', '05': 'batna',
  '06': 'bejaia', '07': 'biskra', '08': 'bechar', '09': 'blida', '10': 'bouira',
  '11': 'tamanrasset', '12': 'tebessa', '13': 'tlemcen', '14': 'tiaret', '15': 'tizi-ouzou',
  '16': 'alger', '17': 'djelfa', '18': 'jijel', '19': 'setif', '20': 'saida',
  '21': 'skikda', '22': 'sidi-bel-abbes', '23': 'annaba', '24': 'guelma', '25': 'constantine',
  '26': 'medea', '27': 'mostaganem', '28': 'msila', '29': 'mascara', '30': 'ouargla',
  '31': 'oran', '32': 'el-bayadh', '33': 'illizi', '34': 'bordj-bou-arreridj', '35': 'boumerdes',
  '36': 'el-tarf', '37': 'tindouf', '38': 'tissemsilt', '39': 'el-oued', '40': 'khenchela',
  '41': 'souk-ahras', '42': 'tipaza', '43': 'mila', '44': 'ain-defla', '45': 'naama',
  '46': 'ain-temouchent', '47': 'ghardaia', '48': 'relizane',
};

const SPECIALTY_SLUG_BY_API = {
  general: 'generaliste', cardiology: 'cardiologue', dermatology: 'dermatologue',
  gynecology: 'gynecologue', ophthalmology: 'ophtalmologue', pediatrics: 'pediatre',
  dentistry: 'dentiste', orthopedics: 'orthopediste', psychiatry: 'psychiatre',
  neurology: 'neurologue', urology: 'urologue', ent: 'orl', radiology: 'radiologue',
  gastroenterology: 'gastro-enterologue', pneumology: 'pneumologue',
  general_surgery: 'chirurgien', endocrinology: 'endocrinologue',
  anesthesiology: 'anesthesiste', rheumatology: 'rhumatologue',
  physical_medicine: 'medecin-physique', nephrology: 'nephrologue',
  neurosurgery: 'neurochirurgien',
};

// Titre du praticien (nom de metier), distinct du nom de la specialite,
// pour des phrases grammaticalement correctes ("consulter un cardiologue",
// pas "consulter une cardiologie").
const PRACTITIONER_TITLE = {
  general: { fr: 'médecin généraliste', ar: 'طبيب عام' },
  cardiology: { fr: 'cardiologue', ar: 'طبيب قلب' },
  dermatology: { fr: 'dermatologue', ar: 'طبيب جلدية' },
  gynecology: { fr: 'gynécologue', ar: 'طبيب نساء' },
  ophthalmology: { fr: 'ophtalmologue', ar: 'طبيب عيون' },
  pediatrics: { fr: 'pédiatre', ar: 'طبيب أطفال' },
  dentistry: { fr: 'dentiste', ar: 'طبيب أسنان' },
  orthopedics: { fr: 'orthopédiste', ar: 'جراح عظام' },
  psychiatry: { fr: 'psychiatre', ar: 'طبيب نفسي' },
  neurology: { fr: 'neurologue', ar: 'طبيب أعصاب' },
  urology: { fr: 'urologue', ar: 'طبيب مسالك بولية' },
  ent: { fr: 'ORL', ar: 'طبيب أنف أذن حنجرة' },
  radiology: { fr: 'radiologue', ar: 'طبيب أشعة' },
  gastroenterology: { fr: 'gastro-entérologue', ar: 'طبيب جهاز هضمي' },
  pneumology: { fr: 'pneumologue', ar: 'طبيب رئة' },
  general_surgery: { fr: 'chirurgien général', ar: 'جراح عام' },
  endocrinology: { fr: 'endocrinologue', ar: 'طبيب غدد صماء' },
  anesthesiology: { fr: 'anesthésiste', ar: 'طبيب تخدير' },
  rheumatology: { fr: 'rhumatologue', ar: 'طبيب روماتيزم' },
  physical_medicine: { fr: 'médecin en médecine physique', ar: 'طبيب طب طبيعي' },
  nephrology: { fr: 'néphrologue', ar: 'طبيب كلى' },
  neurosurgery: { fr: 'neurochirurgien', ar: 'جراح مخ وأعصاب' },
  internal_medicine: { fr: 'interniste', ar: 'طبيب باطني' },
  allergology: { fr: 'allergologue', ar: 'طبيب حساسية' },
  hematology: { fr: 'hématologue', ar: 'طبيب دم' },
  speech_therapy: { fr: 'orthophoniste', ar: 'أخصائي علاج النطق' },
  physiotherapy: { fr: 'kinésithérapeute', ar: 'أخصائي علاج طبيعي' },
  psychology: { fr: 'psychologue', ar: 'أخصائي نفسي' },
  aesthetic_medicine: { fr: 'médecin esthétique', ar: 'طبيب تجميل' },
};

function getPractitionerTitle(specialtyCode, lang) {
  const entry = PRACTITIONER_TITLE[specialtyCode];
  if (!entry) return null;
  return lang === 'ar' ? entry.ar : entry.fr;
}

/**
 * Construit les questions/reponses FAQ pour une fiche medecin, dans la
 * langue active. Contenu genere a partir des donnees reelles du medecin
 * (nom, adresse, specialite, statut en ligne) - jamais de fausses infos.
 */
function buildFaqItems(doctor, specialtyName, wilaya, isOnline, specialtyCode, lang) {
  const name = doctor.name || doctor.full_name || '';
  const isAr = lang === 'ar';
  const reasons = CONSULT_REASONS[specialtyCode] || DEFAULT_REASONS;
  const reasonList = isAr ? reasons.ar : reasons.fr;

  const items = [];

  items.push({
    q: isAr ? `كيف يمكن الاتصال بـ ${name} ؟` : `Comment contacter ${name} ?`,
    a: isOnline
      ? (isAr
          ? `يمكنكم أخذ موعد مباشرة عبر الإنترنت مع ${name} من هذه الصفحة، أو الاتصال بالرقم ${doctor.phone || ''}.`
          : `Vous pouvez prendre rendez-vous en ligne directement avec ${name} depuis cette page, ou l'appeler au ${doctor.phone || ''}.`)
      : (isAr
          ? `يمكنكم الاتصال بـ ${name} على الرقم ${doctor.phone || ''} لأخذ موعد.`
          : `Vous pouvez contacter ${name} par téléphone au ${doctor.phone || ''} pour prendre rendez-vous.`),
  });

  if (doctor.address) {
    items.push({
      q: isAr ? `أين يقع عيادة ${name} ؟` : `Où se trouve le cabinet de ${name} ?`,
      a: isAr
        ? `يقع عيادة ${name} في ${doctor.address}, ${wilaya}.`
        : `Le cabinet de ${name} se trouve à ${doctor.address}, ${wilaya}.`,
    });
  }

  items.push({
    q: isAr ? `ما هو تخصص ${name} ؟` : `Quelle est la spécialité de ${name} ?`,
    a: isAr
      ? `${name} مرجعي في تخصص ${specialtyName}.`
      : `${name} est référencé(e) comme spécialiste en ${specialtyName}.`,
  });

  const reasonList2 = reasonList;
  const practitionerTitle = getPractitionerTitle(specialtyCode, lang) || specialtyName.toLowerCase();
  items.push({
    q: isAr ? `متى يجب استشارة ${practitionerTitle} ؟` : `Quand consulter un(e) ${practitionerTitle} ?`,
    a: isAr
      ? `من الأسباب الشائعة للاستشارة: ${reasonList2.join('، ')}. هذه المعلومات إرشادية ولا تغني عن الرأي الطبي.`
      : `Parmi les motifs fréquents de consultation : ${reasonList2.join(', ')}. Ces informations sont indicatives et ne remplacent pas un avis médical.`,
  });

  items.push({
    q: isAr ? `هل حجز المواعيد عبر الإنترنت متاح مع ${name} ؟` : `La prise de rendez-vous en ligne est-elle disponible avec ${name} ?`,
    a: isOnline
      ? (isAr
          ? `نعم، ${name} متاح لحجز المواعيد عبر الإنترنت مباشرة على طبيبك.`
          : `Oui, ${name} est disponible pour la prise de rendez-vous en ligne directement sur Tabbibak.`)
      : (isAr
          ? `لا، حاليا يجب الاتصال بـ ${name} هاتفيا لأخذ موعد.`
          : `Non, pour le moment il faut contacter ${name} par téléphone pour prendre rendez-vous.`),
  });

  return items;
}

function getReasonTags(specialtyCode, lang) {
  const reasons = CONSULT_REASONS[specialtyCode] || DEFAULT_REASONS;
  return lang === 'ar' ? reasons.ar : reasons.fr;
}

// Alias connus pour chaque specialite (le champ "specialty" en base n'est pas
// toujours un code canonique - certaines fiches importees stockent le libelle
// brut en francais). On normalise vers le code canonique avant tout lookup
// dans SPECIALTIES / PRACTITIONER_TITLE / CONSULT_REASONS.
const SPECIALTY_ALIASES = {
  general: ['general', 'médecine générale', 'medecine generale', 'medecine-generale', 'médecin généraliste', 'generaliste'],
  cardiology: ['cardiology', 'cardiologie', 'cardiologue'],
  dermatology: ['dermatology', 'dermatologie', 'dermatologue'],
  gynecology: ['gynecology', 'gynécologie', 'gynecologie', 'gynécologue', 'gyneco-obstetrique', 'gynéco-obstetrique'],
  ophthalmology: ['ophthalmology', 'ophtalmologie', 'ophtalmologue'],
  pediatrics: ['pediatrics', 'pédiatrie', 'pediatrie', 'pédiatre', 'pediatre'],
  dentistry: ['dentistry', 'chirurgie dentaire', 'chirurgie-dentaire', 'médecine dentaire', 'dentiste'],
  orthopedics: ['orthopedics', 'orthopédie', 'orthopedie', 'chirurgie orthopédique', 'chirurgien orthopédiste', 'traumatologue', 'traumatologie'],
  psychiatry: ['psychiatry', 'psychiatrie', 'psychiatre', 'neuro-psychiatrie'],
  neurology: ['neurology', 'neurologie', 'neurologue'],
  urology: ['urology', 'urologie', 'urologue', 'chirurgie urologique'],
  ent: ['ent', 'orl', 'oto-rhino-laryngologie'],
  radiology: ['radiology', 'radiologie', 'radiologue', 'imagerie médicale'],
  gastroenterology: ['gastroenterology', 'gastro-entérologie', 'gastro-enterologie', 'gastro entérologie', 'gastro-entérologue'],
  pneumology: ['pneumology', 'pneumologie', 'pneumologue', 'pneumo-phtisiologue', 'pneumo-phtisiologie'],
  general_surgery: ['general_surgery', 'chirurgie générale', 'chirurgie generale', 'chirurgien', 'chirurgien général'],
  endocrinology: ['endocrinology', 'endocrinologie', 'endocrinologue', 'diabétologie', 'diabetologie'],
  anesthesiology: ['anesthesiology', 'anesthésiologie', 'anesthesiologie', 'anesthésiste', 'anesthesiste', 'anesthésie-réanimation'],
  rheumatology: ['rheumatology', 'rhumatologie', 'rhumatologue'],
  physical_medicine: ['physical_medicine', 'médecine physique', 'medecine physique', 'médecine physique et réadaptation', 'rééducation fonctionnelle', 'reeducation fonctionnelle'],
  nephrology: ['nephrology', 'néphrologie', 'nephrologie', 'néphrologue', 'nephrologue'],
  neurosurgery: ['neurosurgery', 'neurochirurgie', 'neurochirurgien'],
  internal_medicine: ['internal_medicine', 'médecine interne', 'medecine interne', 'interniste'],
  allergology: ['allergology', 'allergologie', 'allergologue'],
  hematology: ['hematology', 'hématologie', 'hematologie', 'hématologue', 'hematologue'],
  speech_therapy: ['speech_therapy', 'orthophonie', 'orthophoniste'],
  physiotherapy: ['physiotherapy', 'kinésithérapie', 'kinesitherapie', 'kinésithérapeute', 'kinesitherapeute'],
  psychology: ['psychology', 'psychologie', 'psychologue'],
  aesthetic_medicine: ['aesthetic_medicine', 'médecine esthétique', 'medecine esthetique'],
};

function normalizeSpecialtyCode(raw) {
  if (!raw) return raw;
  const cleaned = String(raw).trim().toLowerCase();
  for (const [code, aliases] of Object.entries(SPECIALTY_ALIASES)) {
    if (code === cleaned) return code;
    if (aliases.includes(cleaned)) return code;
  }
  return raw;
}

const StarRating = ({ rating, size = 'w-4 h-4', interactive = false, onChange }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star
        key={i}
        className={`${size} ${
          i <= rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-[#E2E8F0]'
        } ${interactive ? 'cursor-pointer hover:text-[#F59E0B]' : ''}`}
        onClick={() => interactive && onChange?.(i)}
      />
    ))}
  </div>
);

const FaqAccordion = ({ items }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="divide-y divide-[#E2E8F0]">
      {items.map((item, idx) => (
        <div key={idx} className="py-2">
          <button
            className="w-full flex items-center justify-between text-left py-3 gap-4"
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            data-testid={`faq-question-${idx}`}
          >
            <span className="font-medium text-[#0F172A]">{item.q}</span>
            <ChevronDown
              className={`w-5 h-5 flex-shrink-0 text-[#64748B] transition-transform ${
                openIndex === idx ? 'rotate-180' : ''
              }`}
            />
          </button>
          {openIndex === idx && (
            <p className="text-[#64748B] pb-3 leading-relaxed">{item.a}</p>
          )}
        </div>
      ))}
    </div>
  );
};

const DoctorProfile = () => {
  const { doctorId, slug } = useParams();
  const resolvedId = doctorId || null;
  const resolvedSlug = slug || null;
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { t, lang } = useLanguage();
  const dateFnsLocale = lang === 'ar' ? ar : fr;

  const [authDialogOpen, setAuthDialogOpen] = useState(false);
  const [doctor, setDoctor] = useState(null);
  const effectiveId = doctorId || doctor?.doctor_id;
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  // Correctif (demande Nadjib, 20/09/2026) : types de consultation
  // autorises PAR CRENEAU ("in_person"/"video"), renvoyes par le serveur -
  // voir GET /api/doctors/{id}/slots (slot_types).
  const [slotTypes, setSlotTypes] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingDialogOpen, setBookingDialogOpen] = useState(false);
  const [reason, setReason] = useState('');
  // Teleconsultation (idee CGM/ClickDoc) : uniquement propose au patient si
  // le medecin l'a lui-meme activee (doctor.video_consultation_enabled,
  // routes/doctors.py) - revalide cote serveur de toute facon.
  const [consultationType, setConsultationType] = useState('in_person');
  // Correctif (20/09/2026) : le champ "reference de virement" avait disparu du
  // formulaire de reservation alors que le serveur l'exige toujours pour une
  // teleconsultation (routes/appointments.py) - le patient voyait donc
  // l'erreur "Merci d'indiquer la reference de votre virement" sans avoir le
  // moindre champ pour la saisir, ce qui bloquait TOUTE reservation video.
  const [paymentReference, setPaymentReference] = useState('');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, review_count: 0 });
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [claimDialogOpen, setClaimDialogOpen] = useState(false);
  const [claimEmail, setClaimEmail] = useState('');
  const [claimPhone, setClaimPhone] = useState('');
  const [claimStep, setClaimStep] = useState('form');
  const [claimLoading, setClaimLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(resolvedSlug ? `${API_URL}/api/doctors/slug/${resolvedSlug}` : `${API_URL}/api/doctors/${doctorId}`)
      .then((r) => setDoctor(r.data))
      .catch(() => {
        toast.error('Not found');
        navigate('/search');
      })
      .finally(() => setLoading(false));
  }, [doctorId, resolvedSlug, navigate]);

  useEffect(() => {
    // Correctif : cet effet partait des le montage, avant que "doctor" (et
    // donc effectiveId, qui en depend sur la route par slug) soit resolu —
    // provoquant un premier appel GET /api/doctors/undefined/reviews (404)
    // systematique sur chaque fiche medecin, avant le bon appel une fraction
    // de seconde plus tard. Meme garde que l'effet des creneaux juste
    // en-dessous.
    if (!effectiveId) return;
    axios
      .get(`${API_URL}/api/doctors/${effectiveId}/reviews`)
      .then((r) => {
        setReviews(r.data.reviews || []);
        setReviewStats({
          average_rating: r.data.average_rating || 0,
          review_count: r.data.review_count || 0,
        });
      })
      .catch(() => {});
  }, [doctorId, effectiveId]);

  useEffect(() => {
    if (!effectiveId || !doctor?.is_registered || doctor?.status !== 'approved') return;

    setLoadingSlots(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    axios
      .get(`${API_URL}/api/doctors/${effectiveId}/slots?date=${dateStr}`)
      .then((r) => {
        const fetchedSlots = r.data.slots || [];
        // Correctif (demande Nadjib, 20/09/2026) : "slot_types" precise,
        // creneau par creneau, si "in_person" et/ou "video" est propose -
        // avant, un medecin qui activait la teleconsultation la rendait
        // disponible sur N'IMPORTE LEQUEL de ses creneaux, meme ceux ou il
        // n'est pas physiquement au cabinet.
        setSlotTypes(r.data.slot_types || {});
        const todayStr = format(new Date(), 'yyyy-MM-dd');

        if (dateStr !== todayStr) {
          setSlots(fetchedSlots);
          return;
        }

        const currentTime = format(new Date(), 'HH:mm');
        const futureSlots = fetchedSlots.filter((slot) => slot > currentTime);

        setSlots(futureSlots);
      })
      .catch(() => { setSlots([]); setSlotTypes({}); })
      .finally(() => setLoadingSlots(false));
  }, [doctor, doctorId, effectiveId, selectedDate]);

  // Correctif : le type de consultation choisi (cabinet/video) doit rester
  // valide pour le creneau SELECTIONNE - sans ca, choisir "video" pour un
  // creneau puis en choisir un autre qui ne propose pas la video gardait
  // "video" coche silencieusement (rejete seulement a la confirmation,
  // cote serveur). On corrige automatiquement des que necessaire.
  useEffect(() => {
    if (!selectedSlot) return;
    const allowed = slotTypes[selectedSlot] || ['in_person'];
    if (!allowed.includes(consultationType)) {
      setConsultationType(allowed[0] || 'in_person');
    }
  }, [selectedSlot, slotTypes, consultationType]);

  const handleBooking = async () => {
    if (!isAuthenticated) {
      toast.error(t('dp_login_required'));
      navigate('/login');
      return;
    }

    // Meme regle que le serveur (routes/appointments.py) : une teleconsultation
    // n'est confirmable qu'avec une reference de virement. On la verifie ici
    // pour un retour immediat, sans aller-retour reseau.
    if (consultationType === 'video' && !paymentReference.trim()) {
      toast.error(t('dp_payment_reference_required'));
      return;
    }

    setBookingLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/appointments`,
        {
          doctor_id: effectiveId,
          date: format(selectedDate, 'yyyy-MM-dd'),
          time: selectedSlot,
          reason,
          consultation_type: consultationType,
          payment_reference: consultationType === 'video' ? paymentReference.trim() : null,
        },
        {
          withCredentials: true,
          headers: {},
        }
      );

      toast.success(t('dp_booking_success'));
      setBookingDialogOpen(false);
      setSelectedSlot(null);
      setReason('');
      setConsultationType('in_person');
      setPaymentReference('');

      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const res = await axios.get(`${API_URL}/api/doctors/${effectiveId}/slots?date=${dateStr}`);

      const fetchedSlots = res.data.slots || [];
      const todayStr = format(new Date(), 'yyyy-MM-dd');

      if (dateStr !== todayStr) {
        setSlots(fetchedSlots);
      } else {
        const currentTime = format(new Date(), 'HH:mm');
        const futureSlots = fetchedSlots.filter((slot) => slot > currentTime);
        setSlots(futureSlots);
      }
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : t('dp_booking_error'));
      try {
        const dateStr = format(selectedDate, 'yyyy-MM-dd');
        const res = await axios.get(`${API_URL}/api/doctors/${effectiveId}/slots?date=${dateStr}`);
        const fetchedSlots = res.data.slots || [];
        const todayStr = format(new Date(), 'yyyy-MM-dd');
        if (dateStr !== todayStr) {
          setSlots(fetchedSlots);
        } else {
          const currentTime = format(new Date(), 'HH:mm');
          setSlots(fetchedSlots.filter((slot) => slot > currentTime));
        }
      } catch (_e) {
        // best-effort
      }
    } finally {
      setBookingLoading(false);
    }
  };

  const handleReviewSubmit = async () => {
    if (!newReview.comment.trim()) {
      toast.error(t('dp_review_comment'));
      return;
    }

    setReviewLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/reviews`,
        {
          doctor_id: effectiveId,
          rating: newReview.rating,
          comment: newReview.comment,
        },
        {
          withCredentials: true,
          headers: {},
        }
      );

      toast.success('OK!');
      setReviewDialogOpen(false);
      setNewReview({ rating: 5, comment: '' });

      const r = await axios.get(`${API_URL}/api/doctors/${effectiveId}/reviews`);
      setReviews(r.data.reviews || []);
      setReviewStats({
        average_rating: r.data.average_rating || 0,
        review_count: r.data.review_count || 0,
      });
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Erreur, veuillez reessayer');
    } finally {
      setReviewLoading(false);
    }
  };
  const handleClaim = async () => {
    if (!claimEmail || !claimPhone) { toast.error('Email et téléphone requis'); return; }
    if (!claimEmail.includes('@')) { toast.error('Email invalide'); return; }
    if (claimPhone.length < 8) { toast.error('Numéro de téléphone invalide'); return; }
    setClaimLoading(true);
    try {
      await axios.post(API_URL + '/api/doctors/claim', { email: claimEmail, phone: claimPhone, doctor_id: effectiveId });
      setClaimStep('sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally { setClaimLoading(false); }
  };

  const profileUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareText = doctor ? `${doctor.name} - ${t('dp_book')} | Tabbibak.dz` : 'Tabbibak.dz';

  const shareWhatsApp = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText}\n${profileUrl}`)}`, '_blank');
  };

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl)}`,
      '_blank'
    );
  };

  const copyLink = () => {
    navigator.clipboard.writeText(profileUrl).then(() => {
      setLinkCopied(true);
      toast.success(t('dp_link_copied'));
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-gray-200 rounded w-1/4" />
            <div className="bg-white rounded-2xl p-6 space-y-4">
              <div className="flex gap-4">
                <div className="w-32 h-32 rounded-xl bg-gray-200" />
                <div className="flex-1 space-y-3">
                  <div className="h-6 bg-gray-200 rounded w-1/2" />
                  <div className="h-4 bg-gray-200 rounded w-1/3" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!doctor) return null;

  const normalizedSpecialtyCode = normalizeSpecialtyCode(doctor.specialty);
  const spec = SPECIALTIES[normalizedSpecialtyCode] || {
    name_fr: doctor.specialty,
    name_ar: doctor.specialty,
  };
  const specialtyName = lang === 'ar' ? spec.name_ar : spec.name_fr;
  const wilaya = WILAYAS[doctor.wilaya_code] || doctor.wilaya_code;
  const isOnline = doctor.is_registered && doctor.status === 'approved';

  const seoTitle = `${(doctor.name || doctor.full_name || 'Médecin')} – ${specialtyName || ''} ${wilaya || ''} | Tabbibak`;
  const seoDescription = `Prenez rendez-vous avec ${doctor.name || doctor.full_name || 'ce médecin'}, ${specialtyName || ''} à ${wilaya || ''}. Consultation disponible sur Tabbibak.dz`;
  const seoCanonicalUrl = doctor.slug ? `https://tabbibak.com/medecin/${doctor.slug}` : `https://tabbibak.com/doctor/${doctor.doctor_id}`;
  const seoImage = doctor.photo_url || 'https://customer-assets.emergentagent.com/job_tabbibak-algeria/artifacts/bch5rgxl_radja.jpg';

  const faqItems = buildFaqItems(doctor, specialtyName, wilaya, isOnline, normalizedSpecialtyCode, lang);
  const reasonTags = getReasonTags(normalizedSpecialtyCode, lang);
  const wilayaSlug = WILAYA_SLUG_BY_CODE[doctor.wilaya_code];
  const specialtySlug = SPECIALTY_SLUG_BY_API[normalizedSpecialtyCode];
  const canShowRelatedLinks = wilayaSlug && specialtySlug;
  const doctorNameDisplay = doctor.name || doctor.full_name || '';
  const practitionerTitleDisplay = getPractitionerTitle(normalizedSpecialtyCode, lang) || specialtyName.toLowerCase();
  const introText = lang === 'ar'
    ? `${doctorNameDisplay} مرجعي كـ ${practitionerTitleDisplay}. تجمع هذه البطاقة المعلومات العملية المتاحة: العنوان، الهاتف، الاتجاهات، الوصول إلى العيادة.`
    : `${doctorNameDisplay} est référencé(e) comme ${practitionerTitleDisplay}. Cette fiche rassemble les informations pratiques disponibles : adresse, téléphone, itinéraire, accès au cabinet.`;
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a,
      },
    })),
  };

  return (
    <>
    <Helmet>
      <title>{seoTitle}</title>
      <meta name="description" content={seoDescription} />
      <link rel="canonical" href={seoCanonicalUrl} />
      <meta property="og:title" content={seoTitle} />
      <meta property="og:description" content={seoDescription} />
      <meta property="og:url" content={seoCanonicalUrl} />
      <meta property="og:image" content={seoImage} />
      <meta name="twitter:title" content={seoTitle} />
      <meta name="twitter:description" content={seoDescription} />
      <meta name="twitter:image" content={seoImage} />
      <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
    </Helmet>
    <div className="min-h-screen bg-[#F8FAFC]">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 py-8">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-[#64748B] hover:text-[#0596DE] mb-6 transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          {t('dp_back')}
        </button>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 mb-6" data-testid="doctor-profile">
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-shrink-0">
              {doctor.photo_url ? (
                <img
                  src={doctor.photo_url}
                  alt={doctor.name}
                  className="w-32 h-32 rounded-xl object-cover"
                />
              ) : (
                <div className="w-32 h-32 rounded-xl bg-[#E6F4FB] flex items-center justify-center">
                  <span className="text-4xl font-bold text-[#0596DE]">
                    {(doctor.name || '')
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)}
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-[#0F172A] font-['Outfit']">
                    {doctor.name}
                  </h1>
                  <p className="text-lg text-[#0596DE] font-medium mt-1">{specialtyName}</p>

                  {reviewStats.review_count > 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <StarRating rating={Math.round(reviewStats.average_rating)} />
                      <span className="text-sm font-medium text-[#0F172A]">
                        {reviewStats.average_rating}
                      </span>
                      <span className="text-sm text-[#64748B]">
                        ({reviewStats.review_count} {t('doc_rating')})
                      </span>
                    </div>
                  )}
                </div>

                <div className="flex flex-col items-end gap-2">
                  {isOnline ? (
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D1FAE5] text-[#10B981] font-semibold">
                      <span className="w-2 h-2 rounded-full bg-[#10B981] pulse-dot" />
                      {t('dp_available')}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F1F5F9] text-[#475569] font-medium">
                      <Phone className="w-4 h-4" />
                      {t('dp_phone_only')}
                    </span>
                  )}
                  {doctor.video_consultation_enabled && (
                    <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E0F2FE] text-[#0596DE] font-medium text-sm">
                      <Video className="w-4 h-4" />
                      {t('dp_video_available')}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-4 space-y-2 text-[#64748B]">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 flex-shrink-0 text-[#0596DE]" />
                  <span>
                    <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(doctor.address + ' ' + wilaya + ' Algérie')}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#0596DE] hover:underline">{doctor.address} - {wilaya}</a>
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 flex-shrink-0 text-[#0596DE]" />
                  <a href={`tel:${doctor.phone}`} className="hover:text-[#0596DE]">
                    {doctor.phone}
                  </a>
                </div>
              </div>

              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-sm text-[#64748B] font-medium">{t('dp_share')}:</span>

                <button
                  onClick={shareWhatsApp}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#25D366]/10 text-[#25D366] text-sm font-medium hover:bg-[#25D366]/20 transition-colors"
                  data-testid="share-whatsapp"
                >
                  <Send className="w-4 h-4" />
                  {t('dp_share_whatsapp')}
                </button>

                <button
                  onClick={shareFacebook}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#1877F2]/10 text-[#1877F2] text-sm font-medium hover:bg-[#1877F2]/20 transition-colors"
                  data-testid="share-facebook"
                >
                  <Send className="w-4 h-4" />
                  {t('dp_share_facebook')}
                </button>

                <button
                  onClick={copyLink}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#64748B]/10 text-[#64748B] text-sm font-medium hover:bg-[#64748B]/20 transition-colors"
                  data-testid="share-copy-link"
                >
                  {linkCopied ? (
                    <Check className="w-4 h-4 text-[#10B981]" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                  {t('dp_share_copy')}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          {doctor.bio && (
            <div className="md:col-span-2 bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <h3 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#0596DE]" />
                {t('dp_presentation')}
              </h3>
              <p className="text-[#64748B] leading-relaxed">{doctor.bio}</p>
            </div>
          )}

          {doctor.education && (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <h3 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-[#0596DE]" />
                {t('dp_education')}
              </h3>
              <p className="text-[#64748B]">{doctor.education}</p>
            </div>
          )}

          {doctor.languages?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <h3 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <Globe className="w-5 h-5 text-[#0596DE]" />
                {t('dp_languages')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {doctor.languages.map((l) => (
                  <span key={l} className="px-3 py-1 bg-[#F1F5F9] text-[#475569] rounded-full text-sm">
                    {l}
                  </span>
                ))}
              </div>
            </div>
          )}

          {doctor.accepted_insurance?.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6">
              <h3 className="font-semibold text-[#0F172A] mb-3 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#0596DE]" />
                {t('dp_insurance')}
              </h3>
              <div className="flex flex-wrap gap-2">
                {doctor.accepted_insurance.map((ins) => (
                  <span
                    key={ins}
                    className="px-3 py-1 bg-[#E6F4FB] text-[#0596DE] rounded-full text-sm font-medium"
                  >
                    {ins}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {isOnline ? (
          <div
            className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 mb-6"
            data-testid="booking-section"
          >
            <h2 className="text-xl font-bold text-[#0F172A] font-['Outfit'] mb-6 flex items-center gap-2">
              <CalendarIcon className="w-6 h-6 text-[#0596DE]" />
              {t('dp_book')}
            </h2>

            <div className="grid md:grid-cols-2 gap-8">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                disabled={(d) => isBefore(d, startOfDay(new Date()))}
                locale={dateFnsLocale}
                className="rounded-xl border"
              />

              <div>
                <h3 className="font-semibold text-[#0F172A] mb-4">
                  {t('dp_slots_on')} {format(selectedDate, 'EEEE d MMMM', { locale: dateFnsLocale })}
                </h3>

                {loadingSlots ? (
                  <div className="grid grid-cols-3 gap-2">
                    {[...Array(6)].map((_, i) => (
                      <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : slots.length === 0 ? (
                  <div className="text-center py-8 text-[#64748B]">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
                    <p>{t('dp_no_slots')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {slots.map((slot) => (
                      <Button
                        key={slot}
                        variant={selectedSlot === slot ? 'default' : 'outline'}
                        className={`rounded-lg ${selectedSlot === slot ? 'bg-[#0596DE]' : ''}`}
                        disabled={loadingSlots || bookingLoading}
                        onClick={() => {
                          if (bookingLoading) return;

                          if (!isAuthenticated) {
                            toast.error(t('dp_login_required'));

                            setTimeout(() => {
                              navigate('/login', { state: { from: window.location.pathname } });
                            }, 1500);

                            return;
                          }

                          // Correctif : un compte medecin (ou tout autre role
                          // non-patient) pouvait reserver un RDV chez un
                          // confrere via cette page publique - le RDV etait
                          // bien enregistre cote serveur mais ce parcours
                          // n'est ni teste ni pense pour ca (pas de "mes
                          // rendez-vous" cote medecin reservant, confusion
                          // sur qui voit quoi). Seul un compte patient peut
                          // reserver ici desormais - meme regle cote serveur
                          // (create_appointment, routes/appointments.py).
                          if (user?.role !== 'patient') {
                            toast.error(t('dp_patient_account_required'));
                            return;
                          }

                          setSelectedSlot(slot);
                          setBookingDialogOpen(true);
                        }}
                      >
                        {slot}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div
            className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 text-center mb-6"
            data-testid="phone-contact-section"
          >
            <Phone className="w-16 h-16 text-[#0596DE] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[#0F172A] font-['Outfit'] mb-2">
              {t('dp_contact')}
            </h2>
            <p className="text-[#64748B] mb-6">{t('dp_no_online')}</p>
            <a href={`tel:${doctor.phone}`}>
              <Button
                size="lg"
                className="rounded-full bg-[#0596DE] hover:bg-[#047CBA]"
                data-testid="call-doctor-btn"
              >
                <Phone className="w-5 h-5 mr-2" />
                {t('dp_call')} {doctor.phone}
              </Button>
            </a>
            {doctor.is_registered !== true && (
              <div className="mt-6 border-t border-[#E2E8F0] pt-4 text-center">
                {doctor.entity_type ? (
                  <>
                    <p className="text-sm text-[#64748B] mb-1">
                      Ce {String(doctor.entity_type).toLowerCase()} est le votre ?
                    </p>
                    <p className="text-xs text-[#94A3B8] mb-3">
                      Ouvrez votre espace : equipe, horaires, photos et prise de rendez-vous.
                    </p>
                    <button onClick={() => setClaimDialogOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E6F4FB] text-[#0596DE] text-sm font-medium hover:bg-[#0596DE] hover:text-white transition-colors">
                      <UserCheck className="w-4 h-4" />
                      Revendiquer cet etablissement
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-[#64748B] mb-3">Vous etes ce medecin ?</p>
                    <button onClick={() => setClaimDialogOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E6F4FB] text-[#0596DE] text-sm font-medium hover:bg-[#0596DE] hover:text-white transition-colors">
                      <UserCheck className="w-4 h-4" />
                      Reclamer mon profil
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8" data-testid="reviews-section">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-[#0F172A] font-['Outfit'] flex items-center gap-2">
              <Star className="w-6 h-6 text-[#F59E0B]" />
              {t('dp_reviews')}
              {reviewStats.review_count > 0 && (
                <span className="text-base font-normal text-[#64748B]">
                  ({reviewStats.review_count})
                </span>
              )}
            </h2>

            {isAuthenticated && user?.role === 'patient' && (
              <Button
                variant="outline"
                className="rounded-full"
                onClick={() => setReviewDialogOpen(true)}
                data-testid="write-review-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {t('dp_write_review')}
              </Button>
            )}
          </div>

          {reviewStats.review_count > 0 && (
            <div className="flex items-center gap-6 mb-6 p-4 bg-[#F8FAFC] rounded-xl">
              <div className="text-center">
                <div className="text-4xl font-bold text-[#0F172A]">
                  {reviewStats.average_rating}
                </div>
                <StarRating rating={Math.round(reviewStats.average_rating)} />
              </div>
              <div className="text-[#64748B]">
                <p className="font-medium text-[#0F172A]">
                  {reviewStats.review_count} {t('doc_rating')}
                </p>
                <p className="text-sm">{t('dp_avg_rating')}</p>
              </div>
            </div>
          )}

          {reviews.length === 0 ? (
            <div className="text-center py-8 text-[#64748B]">
              <Star className="w-12 h-12 mx-auto mb-2 text-[#E2E8F0]" />
              <p>{t('dp_no_reviews')}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.review_id} className="border-b border-[#E2E8F0] pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[#E6F4FB] flex items-center justify-center">
                        <span className="text-sm font-bold text-[#0596DE]">
                          {review.patient_name?.split(' ').map((n) => n[0]).join('').slice(0, 2) || 'P'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-[#0F172A]">{review.patient_name}</p>
                        <StarRating rating={review.rating} size="w-3 h-3" />
                      </div>
                    </div>
                    <span className="text-xs text-[#64748B]">
                      {review.created_at
                        ? format(parseISO(review.created_at), 'dd MMM yyyy', { locale: dateFnsLocale })
                        : ''}
                    </span>
                  </div>
                  <p className="text-[#64748B] pl-[52px]">{review.comment}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 mt-6" data-testid="doctor-intro-section">
          <p className="text-[#64748B] leading-relaxed mb-6">{introText}</p>

          <h3 className="font-semibold text-[#0F172A] mb-3">
            {lang === 'ar' ? `متى تجب استشارة ${practitionerTitleDisplay} ؟` : `Quand consulter un(e) ${practitionerTitleDisplay} ?`}
          </h3>
          <div className="flex flex-wrap gap-2 mb-2">
            {reasonTags.map((reason, idx) => (
              <span
                key={idx}
                className="px-3 py-1.5 bg-[#F1F5F9] text-[#475569] rounded-full text-sm"
              >
                {reason}
              </span>
            ))}
          </div>

          {canShowRelatedLinks && (
            <div className="mt-6 pt-6 border-t border-[#E2E8F0] flex flex-wrap gap-x-6 gap-y-2 text-sm">
              <a
                href={`/medecins/${wilayaSlug}/${specialtySlug}`}
                className="text-[#0596DE] hover:underline font-medium"
              >
                {lang === 'ar' ? `${practitionerTitleDisplay} آخرون في ${wilaya}` : `Autres ${practitionerTitleDisplay}s à ${wilaya}`}
              </a>
            </div>
          )}
        </div>

        <div className="bg-white rounded-2xl border border-[#E2E8F0] p-6 md:p-8 mt-6" data-testid="faq-section">
          <h2 className="text-xl font-bold text-[#0F172A] font-['Outfit'] mb-2 flex items-center gap-2">
            <HelpCircle className="w-6 h-6 text-[#0596DE]" />
            {lang === 'ar' ? 'أسئلة شائعة' : 'Questions fréquentes'}
          </h2>
          <FaqAccordion items={faqItems} />
        </div>
      </div>

      <Dialog open={bookingDialogOpen} onOpenChange={setBookingDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{t('dp_confirm_title')}</DialogTitle>
            <DialogDescription>
              {t('dp_confirm_with')} {doctor?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="bg-[#F8FAFC] rounded-lg p-4 space-y-2">
              <p className="text-sm text-[#64748B]">{t('dp_date_time')}</p>
              <p className="font-semibold text-[#0F172A]">
                {format(selectedDate, 'EEEE d MMMM yyyy', { locale: dateFnsLocale })} - {selectedSlot}
              </p>
            </div>

            {/* Correctif (demande Nadjib, 20/09/2026) : le choix Cabinet/Video
                se basait uniquement sur l'interrupteur global du medecin -
                un patient pouvait choisir "en cabinet" sur un creneau ou le
                medecin ne propose QUE la video (ex: un creneau du soir),
                puisque rien ne restreignait le choix au CRENEAU precis.
                slotTypes[selectedSlot] (renvoye par le serveur, voir
                GET .../slots) dit desormais precisement ce que CE creneau
                autorise. Choix affiche seulement si les deux options sont
                valides pour ce creneau ; sinon consultationType est deja
                force sur l'unique option valide (effet ci-dessus). */}
            {(() => {
              const currentSlotTypes = selectedSlot ? (slotTypes[selectedSlot] || ['in_person']) : ['in_person'];
              if (currentSlotTypes.length > 1) {
                return (
                  <div>
                    <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                      {t('dp_consultation_type_label')}
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setConsultationType('in_person')}
                        className={`rounded-lg border px-3 py-2 text-sm text-left ${consultationType === 'in_person' ? 'border-[#0596DE] bg-[#F0F9FF] text-[#0596DE] font-medium' : 'border-[#E2E8F0] text-[#64748B]'}`}
                      >
                        {t('dp_consultation_type_in_person')}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConsultationType('video')}
                        className={`rounded-lg border px-3 py-2 text-sm text-left ${consultationType === 'video' ? 'border-[#0596DE] bg-[#F0F9FF] text-[#0596DE] font-medium' : 'border-[#E2E8F0] text-[#64748B]'}`}
                      >
                        {t('dp_consultation_type_video')}
                      </button>
                    </div>
                  </div>
                );
              }
              if (currentSlotTypes[0] === 'video') {
                return (
                  <div className="flex items-center gap-2 text-sm text-[#0596DE] bg-[#F0F9FF] rounded-lg px-3 py-2">
                    <Video className="w-4 h-4 flex-shrink-0" />
                    {t('dp_slot_video_only_notice')}
                  </div>
                );
              }
              return null;
            })()}

            {/* Correctif (20/09/2026) : bloc paiement de la teleconsultation.
                Le serveur refuse une reservation "video" sans reference de
                virement ; sans ce bloc le patient recevait l'erreur sans
                aucun champ pour la saisir. */}
            {consultationType === 'video' && (
              <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-lg p-3 text-sm text-[#92400E] space-y-2">
                {doctor?.video_payment_ccp ? (
                  <p>{t('dp_video_payment_notice_prefix')} <strong>{doctor.video_payment_ccp}</strong></p>
                ) : (
                  <p>{t('dp_video_payment_no_ccp')}</p>
                )}
                <label className="text-xs font-medium block">{t('dp_payment_reference_label')}</label>
                <Input
                  value={paymentReference}
                  onChange={(e) => setPaymentReference(e.target.value)}
                  placeholder={t('dp_payment_reference_placeholder')}
                  className="rounded-lg bg-white"
                  data-testid="booking-payment-reference"
                />
              </div>
            )}

            <div>
              <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                {t('dp_reason')}
              </label>
              <Textarea
                placeholder={t('dp_reason_placeholder')}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="booking-reason"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBookingDialogOpen(false)}>
              {t('dp_cancel')}
            </Button>
            <Button
              onClick={handleBooking}
              disabled={bookingLoading}
              className="bg-[#0596DE] hover:bg-[#047CBA]"
              data-testid="confirm-booking-btn"
            >
              {bookingLoading ? t('dp_booking') : t('dp_confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={claimDialogOpen} onOpenChange={(open) => { setClaimDialogOpen(open); if (!open) setClaimStep('form'); }}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Reclamer mon profil</DialogTitle>
            <DialogDescription>
              {claimStep === 'sent' ? 'Email envoye ! Verifiez votre boite mail.' : 'Entrez votre email et mot de passe pour activer ce profil.'}
            </DialogDescription>
          </DialogHeader>
          {claimStep === 'form' ? (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">Email professionnel</label>
                <input type="email" value={claimEmail} onChange={(e) => setClaimEmail(e.target.value)} placeholder="votre@email.com" className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0F172A] mb-2 block">Téléphone professionnel</label>
                <input type="tel" value={claimPhone} onChange={(e) => setClaimPhone(e.target.value)} placeholder="0555 12 34 56" className="w-full border border-[#E2E8F0] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0596DE]" />
              </div>
            </div>
          ) : (
            <div className="py-6 text-center">
              <div className="w-16 h-16 rounded-full bg-[#D1FAE5] flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-[#10B981]" />
              </div>
              <p className="text-[#64748B]">Votre demande a été enregistrée. Notre équipe vous contactera sous 48h pour vérifier votre identité, puis vous recevrez un email d'activation.</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setClaimDialogOpen(false); setClaimStep('form'); }}>Fermer</Button>
            {claimStep === 'form' && (
              <Button onClick={handleClaim} disabled={claimLoading} className="bg-[#0596DE] hover:bg-[#047CBA]">{claimLoading ? 'Envoi...' : 'Envoyer la demande'}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>{t('dp_review_title')}</DialogTitle>
            <DialogDescription>
              {t('dp_review_desc')} {doctor?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                {t('dp_review_note')}
              </label>
              <StarRating
                rating={newReview.rating}
                size="w-8 h-8"
                interactive
                onChange={(r) => setNewReview({ ...newReview, rating: r })}
              />
            </div>

            <div>
              <label className="text-sm font-medium text-[#0F172A] mb-2 block">
                {t('dp_review_comment')}
              </label>
              <Textarea
                placeholder={t('dp_review_placeholder')}
                value={newReview.comment}
                onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                rows={4}
                data-testid="review-comment-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              {t('dp_cancel')}
            </Button>
            <Button
              onClick={handleReviewSubmit}
              disabled={reviewLoading}
              className="bg-[#0596DE] hover:bg-[#047CBA]"
              data-testid="submit-review-btn"
            >
              {reviewLoading ? t('dp_review_submitting') : t('dp_review_submit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </>
  );
};

export default DoctorProfile;
